import { E as EVENTS } from './events-58bc9098.js';

class ProductBlockVariantSelector extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      variantButton: "[js-variant-button]",
      variantGroup: "[js-variant-group-container]",
      variantLabelValue: "[js-variant-label-value]",
      variantOption: "[js-variant-option]",
      variantInput: "[js-variant-input]",
      // RK 9/10/25 Modified to support dropdown variant select with siblings.
      noJsVariantSelector: "[js-no-js-variant-selector]",
      variantDropdown: "[js-variant-dropdown]",
      variantDropdownToggle: "[js-variant-dropdown-toggle]",
      variantDropdownPanel: "[js-variant-dropdown-panel]",
      variantDropdownOption: "[js-variant-dropdown-option]",
      variantDropdownCurrent: "[js-variant-dropdown-current]",
      variantDropdownNative: ".variant-dropdown__native",
    };

    this.handleDropdownDocumentClick = this.handleDropdownDocumentClick.bind(this);

    this.dropdownController = null;
    this.dropdownInstances = [];
  }

  connectedCallback() {
    this.controller = new AbortController();

    // Listen for variant change
    this.initActiveVariants();

    // RK 9/19/25 Dropdown accordion: enhance custom UI controls.
    this.initDropdownAccordions();

    // Reinitialize listeners on variant change
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      this.handleRepaint.bind(this),
      { signal: this.controller.signal },
    );
  }

  // TODO: This gets called a lot, see if there's a more performant solution
  initActiveVariants() {
    this.activeVariantsController = new AbortController();

    // Remove no-js variant selector to prevent conflicting id's
    this.noJsVariantSelector = this.querySelector(
      this.selectors.noJsVariantSelector,
    );
    this.noJsVariantSelector?.remove();

    this.variantInputs = this.querySelectorAll(this.selectors.variantInput);
    this.variantInputs.forEach((input) => {
      input.addEventListener("change", this.onVariantChange, {
        signal: this.activeVariantsController.signal,
      });
    });

    // Listeners for tabbing and selecting swatches and chips specifically
    this.variantButton = this.querySelectorAll(this.selectors.variantButton);
    this.variantButton.forEach((button) => {
      button.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();

            const swatchButton = button.getAttribute("js-swatch");
            const chipButton = button.getAttribute("js-chip");

            if (swatchButton !== null) {
              const swatchWrapper = event.target.parentElement;
              const inputHtml = swatchWrapper.previousElementSibling;

              this.onVariantChangeA11y(inputHtml);
            } else if (chipButton !== null) {
              this.onVariantChangeA11y(event.target.previousElementSibling);
            }
          }
        },
        {
          signal: this.activeVariantsController.signal,
        },
      );
    });
  }

  // RK 9/10/25 Modified to support dropdown variant select with siblings.
  onVariantChange(event) {
    // Determine layout from the input/select that triggered the event
    this.variantLayout = event.target.getAttribute("data-variant-layout") ||
      (event.target.tagName === "SELECT" ? "dropdown" : undefined);
    this.swatchOption = event.target.getAttribute("data-swatch-option");

    switch (this.variantLayout) {
      case "chips":
        event.target.parentElement.parentElement
          .querySelector(".chip--selected")
          .classList.remove("chip--selected");

        event.target.parentElement
          .querySelector("[js-chip]")
          .classList.add("chip--selected");
        break

      case "swatches":
        event.target.parentElement.parentElement
          .querySelector(".swatch--selected")
          .classList.remove("swatch--selected");

        event.target.parentElement
          .querySelector("[js-swatch]")
          .classList.add("swatch--selected");
        break

      case "tiles":
        // RK 9/13/25 Tiles: use :checked state; no JS class toggling needed.
        break
    }

    let optionValueElement;
    if (this.variantLayout === "swatches" || this.variantLayout === "chips" || this.variantLayout === "tiles") {
      optionValueElement = event.target;
    } else if (this.variantLayout === "dropdown") {
      optionValueElement = event.target.selectedOptions[0];
    }

    // The current main product can either be the PDP's/Featured Prod or the product within the Quick view
    // TODO: Dry up repetition after binding `this` to onVariantChange to get scope and altering it's internal use of `this`
    const currentMainProductSection = this.closest("[js-product-section]");
    const productWithinQuickView =
      currentMainProductSection.dataset.sectionType === "quick-view-product";

    const variantChangingEvent = new CustomEvent(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      {
        bubbles: true,
        detail: {
          newOptionInput: optionValueElement,
          withinQuickView: productWithinQuickView,
        },
      },
    );

    currentMainProductSection.dispatchEvent(variantChangingEvent);
  }

  onVariantChangeA11y(inputHtml) {
    // The current main product can either be the PDP's/Featured Prod or the product within the Quick view
    const currentMainProductSection = this.closest("[js-product-section]");
    const productWithinQuickView =
      currentMainProductSection.dataset.sectionType === "quick-view-product";

    const variantChangingEvent = new CustomEvent(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      {
        bubbles: true,
        detail: {
          newOptionInput: inputHtml,
          withinQuickView: productWithinQuickView,
        },
      },
    );

    currentMainProductSection.dispatchEvent(variantChangingEvent);
  }

  handleRepaint() {
    this.activeVariantsController.abort();
    this.initActiveVariants();

    // RK 9/19/25 Dropdown accordion: re-initialize enhanced dropdown UI
    this.initDropdownAccordions();
  }

  disconnectedCallback() {
    this.controller.abort();
    this.dropdownController?.abort();
  }

  // RK 9/19/25 Dropdown accordion: initialize enhanced dropdown UI
  initDropdownAccordions() {
    this.dropdownController?.abort();
    this.dropdownController = new AbortController();
    this.dropdownInstances = [];

    const dropdowns = this.querySelectorAll(this.selectors.variantDropdown);
    dropdowns.forEach((dropdown) => {
      const instance = this.setupDropdown(dropdown);
      if (instance) {
        this.dropdownInstances.push(instance);
      }
    });

    if (this.dropdownInstances.length === 0) return;

    document.addEventListener('click', this.handleDropdownDocumentClick, {
      signal: this.dropdownController.signal,
    });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          this.closeAllDropdowns();
        }
      },
      { signal: this.dropdownController.signal },
    );
  }

  setupDropdown(dropdown) {
    const toggle = dropdown.querySelector(this.selectors.variantDropdownToggle);
    const panel = dropdown.querySelector(this.selectors.variantDropdownPanel);
    const currentLabel = dropdown.querySelector(this.selectors.variantDropdownCurrent);
    const nativeSelect = dropdown.querySelector(this.selectors.variantDropdownNative);
    const options = dropdown.querySelectorAll(this.selectors.variantDropdownOption);

    if (!toggle || !panel || !currentLabel || !nativeSelect || options.length === 0) {
      return null;
    }

    const { signal } = this.dropdownController;

    dropdown.classList.add('variant-dropdown--enhanced');

    toggle.hidden = false;
    toggle.removeAttribute('hidden');

    panel.hidden = false;
    panel.removeAttribute('hidden');
    panel.style.maxHeight = '0px';
    panel.style.visibility = 'hidden';
    panel.setAttribute('aria-hidden', 'true');

    nativeSelect.style.display = 'none';
    nativeSelect.setAttribute('aria-hidden', 'true');
    nativeSelect.tabIndex = -1;
    toggle.setAttribute('aria-expanded', 'false');

    toggle.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        const isOpen = dropdown.classList.contains('variant-dropdown--open');
        if (isOpen) {
          this.setDropdownState(dropdown, false);
        } else {
          this.closeAllDropdowns(dropdown);
          this.setDropdownState(dropdown, true, { focusPanel: true });
        }
      },
      { signal },
    );

    toggle.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (!dropdown.classList.contains('variant-dropdown--open')) {
            this.closeAllDropdowns(dropdown);
            this.setDropdownState(dropdown, true, { focusPanel: true });
          }
        }
      },
      { signal },
    );

    panel.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          this.setDropdownState(dropdown, false);
          toggle.focus();
        }
      },
      { signal },
    );

    options.forEach((option) => {
      option.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          if (option.disabled) return;

          this.selectDropdownOption(dropdown, option, nativeSelect);
          this.setDropdownState(dropdown, false);
          toggle.focus();
        },
        { signal },
      );
    });

    nativeSelect.addEventListener(
      'change',
      (event) => {
        this.updateDropdownFromNative(dropdown, event.target);
      },
      { signal },
    );

    this.updateDropdownFromNative(dropdown, nativeSelect);

    return { dropdown, toggle };
  }

  setDropdownState(dropdown, isOpen, { focusPanel = false } = {}) {
    const toggle = dropdown.querySelector(this.selectors.variantDropdownToggle);
    const panel = dropdown.querySelector(this.selectors.variantDropdownPanel);

    if (!toggle || !panel) return;

    const wasOpen = dropdown.classList.contains('variant-dropdown--open');
    dropdown.classList.toggle('variant-dropdown--open', isOpen);
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (!isOpen && !wasOpen) {
      panel.setAttribute('aria-hidden', 'true');
      panel.style.maxHeight = '0px';
      panel.style.visibility = 'hidden';
      return;
    }

    if (isOpen) {
      panel.setAttribute('aria-hidden', 'false');
      panel.style.visibility = 'visible';
      // double RAF to ensure styles apply before measuring scrollHeight
      requestAnimationFrame(() => {
        panel.style.maxHeight = `${panel.scrollHeight}px`;
      });
    } else {
      panel.setAttribute('aria-hidden', 'true');
      panel.style.maxHeight = '0px';

      const onTransitionEnd = (event) => {
        if (event.propertyName !== 'max-height') return;
        panel.style.visibility = 'hidden';
        panel.removeEventListener('transitionend', onTransitionEnd);
      };

      panel.addEventListener('transitionend', onTransitionEnd);
    }

    if (isOpen && focusPanel) {
      const focusTarget = panel.querySelector('.variant-dropdown__option.is-selected:not(:disabled)') ||
        panel.querySelector('.variant-dropdown__option:not(:disabled)');
      focusTarget?.focus();
    }
  }

  closeAllDropdowns(exceptDropdown = null) {
    if (!this.dropdownInstances) return;

    this.dropdownInstances.forEach(({ dropdown }) => {
      if (exceptDropdown && dropdown === exceptDropdown) return;
      this.setDropdownState(dropdown, false);
    });
  }

  selectDropdownOption(dropdown, optionButton, nativeSelect) {
    if (!nativeSelect) return;

    const newValue = optionButton.dataset.value;
    if (!newValue) return;

    const currentValue = nativeSelect.value;
    if (currentValue !== newValue) {
      nativeSelect.value = newValue;
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.updateDropdownFromNative(dropdown, nativeSelect);
    }
  }

  updateDropdownFromNative(dropdown, selectElement) {
    if (!selectElement) return;

    const selectedOption = selectElement.selectedOptions ? selectElement.selectedOptions[0] : null;
    const selectedValue = selectedOption ? selectedOption.value : selectElement.value;
    const currentLabel = dropdown.querySelector(this.selectors.variantDropdownCurrent);
    const optionButtons = dropdown.querySelectorAll(this.selectors.variantDropdownOption);

    optionButtons.forEach((button) => {
      const isSelected = button.dataset.value === selectedValue;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    if (currentLabel && selectedOption) {
      currentLabel.textContent = selectedOption.textContent.trim();
    }

    if (dropdown.classList.contains('variant-dropdown--open')) {
      this.setDropdownState(dropdown, true);
    }
  }

  handleDropdownDocumentClick(event) {
    const target = event.target;
    const dropdown = target.closest(this.selectors.variantDropdown);

    if (!dropdown) {
      this.closeAllDropdowns();
      return;
    }

    this.closeAllDropdowns(dropdown);
  }
}

if (!customElements.get("product-block-variant-selector")) {
  customElements.define(
    "product-block-variant-selector",
    ProductBlockVariantSelector,
  );
}
